/**
 * Contratos de entrada (Zod) de clientes: datos personales, conozca a su
 * cliente (KYC), negocios y referencias.
 */
import { z } from 'zod';
import { keyedEnum } from '../catalogs/enum.js';
import { CLIENT_LANGUAGES, DEFAULT_CLIENT_LANGUAGE } from '../identity/geography.js';
import { coordinatesSchema, cuiSchema, departmentCodeSchema, idSchema, isoDateSchema, moneySchema, nitSchema, optionalText, TEXT_MAX, phoneSchema, clientRequestIdSchema, capturedAtSchema, } from './common.js';
/** Sexo registrado en el DPI. */
export const GENDERS = ['female', 'male'];
/** Catálogo con clave de sexo: `GENDER.female`. */
export const GENDER = keyedEnum(GENDERS);
/** Etiquetas en español de cada sexo. */
export const GENDER_LABELS = { female: 'Mujer', male: 'Hombre' };
/** Estados civiles. */
export const MARITAL_STATUSES = ['single', 'married', 'common_law', 'divorced', 'widowed'];
/** Etiquetas en español de cada estado civil. */
export const MARITAL_STATUS_LABELS = {
    single: 'Soltero(a)',
    married: 'Casado(a)',
    common_law: 'Unido(a)',
    divorced: 'Divorciado(a)',
    widowed: 'Viudo(a)',
};
/** Niveles de escolaridad del sistema educativo guatemalteco. */
export const EDUCATION_LEVELS = ['none', 'primary', 'basic', 'diversified', 'university'];
/** Etiquetas en español de cada nivel de escolaridad. */
export const EDUCATION_LEVEL_LABELS = {
    none: 'Sin escolaridad',
    primary: 'Primaria',
    basic: 'Básicos',
    diversified: 'Diversificado',
    university: 'Universitaria',
};
/** Tenencia de la vivienda. */
export const HOUSING_TYPES = ['own', 'rented', 'family', 'other'];
/** Etiquetas en español de cada tenencia de vivienda. */
export const HOUSING_TYPE_LABELS = {
    own: 'Propia',
    rented: 'Alquilada',
    family: 'Familiar',
    other: 'Otra',
};
/** Sectores económicos del negocio del cliente. */
export const BUSINESS_SECTORS = [
    'commerce',
    'agriculture',
    'livestock',
    'services',
    'manufacturing',
    'crafts',
    'food',
    'transport',
    'other',
];
/** Etiquetas en español de cada sector. */
export const BUSINESS_SECTOR_LABELS = {
    commerce: 'Comercio (tienda, abarrotería, venta en mercado)',
    agriculture: 'Agricultura',
    livestock: 'Pecuario',
    services: 'Servicios',
    manufacturing: 'Producción / manufactura',
    crafts: 'Artesanía y tejidos',
    food: 'Comedor / venta de alimentos',
    transport: 'Transporte',
    other: 'Otro',
};
/** Máximo de dependientes económicos que se registran. */
const MAX_DEPENDENTS = 30;
/** Máximo de años (en la dirección o en operación) que se aceptan. */
const MAX_YEARS = 100;
/** Máximo de empleados de un negocio de microempresa. */
const MAX_BUSINESS_EMPLOYEES = 1000;
/** Longitud máxima de la descripción del origen de fondos. */
const SOURCE_OF_FUNDS_MAX_LENGTH = 300;
/** Campos de dirección compartidos por clientes, negocios, fiadores y agencias. */
export const addressSchema = z.object({
    address: z.string().trim().min(5, 'Dirección muy corta').max(TEXT_MAX.shortText),
    /** "Frente a la iglesia católica, casa de block celeste": así se ubican las direcciones rurales. */
    addressReference: optionalText(TEXT_MAX.shortText),
    department: departmentCodeSchema,
    municipality: z.string().trim().min(2).max(TEXT_MAX.label),
    coordinates: coordinatesSchema.optional(),
});
/** Campos editables de un cliente. */
const clientFields = z.object({
    firstNames: z.string().trim().min(2).max(TEXT_MAX.personName),
    lastNames: z.string().trim().min(2).max(TEXT_MAX.personName),
    cui: cuiSchema,
    nit: nitSchema.optional(),
    birthDate: isoDateSchema,
    gender: z.enum(GENDERS),
    maritalStatus: z.enum(MARITAL_STATUSES),
    educationLevel: z.enum(EDUCATION_LEVELS).optional(),
    dependents: z.number().int().min(0).max(MAX_DEPENDENTS).default(0),
    language: z.enum(CLIENT_LANGUAGES).default(DEFAULT_CLIENT_LANGUAGE),
    phone: phoneSchema,
    alternatePhone: phoneSchema.optional(),
    email: z.string().trim().email().optional(),
    housingType: z.enum(HOUSING_TYPES),
    yearsAtAddress: z.number().int().min(0).max(MAX_YEARS).optional(),
    occupation: z.string().trim().min(2).max(TEXT_MAX.name),
    branchId: idSchema.optional(),
    assignedAdvisorId: idSchema.optional(),
    // Conozca a su cliente (Decreto 67-2001)
    isPoliticallyExposed: z.boolean().default(false),
    politicalExposureDetails: optionalText(TEXT_MAX.shortText),
    sourceOfFunds: z.string().trim().min(3).max(SOURCE_OF_FUNDS_MAX_LENGTH),
    expectedMonthlyPayments: moneySchema.optional(),
    creditBureauConsent: z.boolean().default(false),
});
/** Alta de un cliente (exige consentimiento de tratamiento de datos). */
export const CreateClientSchema = clientFields.merge(addressSchema).extend({
    /** Idempotencia del registro capturado sin conexión. */
    clientRequestId: clientRequestIdSchema,
    /** Momento del registro en el teléfono: fecha del consentimiento del cliente. */
    capturedAt: capturedAtSchema,
    /** Consentimiento informado para tratar datos personales; obligatorio para registrar al cliente. */
    dataProcessingConsent: z.literal(true, {
        errorMap: () => ({ message: 'Se requiere el consentimiento del cliente para el tratamiento de sus datos' }),
    }),
});
/** Edición parcial de un cliente. */
export const UpdateClientSchema = clientFields.merge(addressSchema).partial();
/** Negocio del cliente. */
export const ClientBusinessSchema = z
    .object({
    name: z.string().trim().min(2).max(TEXT_MAX.name),
    sector: z.enum(BUSINESS_SECTORS),
    activityDescription: z.string().trim().min(3).max(TEXT_MAX.shortText),
    nit: nitSchema.optional(),
    hasCommercialLicense: z.boolean().default(false),
    yearsInOperation: z.number().int().min(0).max(MAX_YEARS),
    employees: z.number().int().min(0).max(MAX_BUSINESS_EMPLOYEES).default(0),
    monthlySales: moneySchema.optional(),
    isPrimary: z.boolean().default(true),
})
    .merge(addressSchema);
/** Tipos de referencia del cliente. */
export const REFERENCE_TYPES = ['personal', 'commercial', 'family'];
/** Etiquetas en español de cada tipo de referencia. */
export const REFERENCE_TYPE_LABELS = {
    personal: 'Personal',
    commercial: 'Comercial',
    family: 'Familiar',
};
/** Referencia personal, comercial o familiar. */
export const ClientReferenceSchema = z.object({
    type: z.enum(REFERENCE_TYPES),
    fullName: z.string().trim().min(3).max(TEXT_MAX.name),
    relationship: z.string().trim().min(2).max(TEXT_MAX.label),
    phone: phoneSchema,
    notes: optionalText(TEXT_MAX.shortText),
});
/**
 * Edición parcial de una referencia: se envían solo los campos que cambian,
 * pero al menos uno. Si cambia el nombre o el teléfono, el API retira la
 * verificación porque ya no corresponde a la persona con quien se habló.
 */
export const UpdateClientReferenceSchema = ClientReferenceSchema.extend({
    /** `null` borra las notas; omitirlas las deja como están. */
    notes: optionalText(TEXT_MAX.shortText).nullable(),
})
    .partial()
    .refine((input) => Object.values(input).some((value) => value !== undefined), {
    message: 'Indica al menos un dato de la referencia para modificar',
});
/** Resultado de verificar una referencia. */
export const VerifyReferenceSchema = z.object({
    verified: z.boolean(),
    notes: optionalText(TEXT_MAX.shortText),
});
//# sourceMappingURL=client.js.map